import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiClient } from '@/api/client';
import { wsService } from '@/services/websocket';
import { selectIsAuthenticated } from '@/store/slices/authSlice';

// Fleet-safety knobs. Latency beacons fire exactly when the backend is
// degraded, so they must stay a trickle, not a flood: only ~1% of sessions
// report at all, and a sampled-in session sends at most one beacon per minute.
const BEACON_SESSION_SAMPLE_RATE = 0.01;
const BEACON_MIN_INTERVAL_MS = 60_000;

/**
 * useRealtimeLatencyBeacon
 *
 * Attaches a dedicated listener to the realtime stream for `worker_update`
 * events. When delivery latency exceeds 5000ms, POSTs a beacon to
 * `/api/v1/telemetry/beacon` — only from authenticated sessions, only from
 * the ~1% of sessions sampled in at mount, and at most once per minute.
 *
 * This hook is render-neutral — it does NOT modify any state or trigger
 * re-renders. It only sends telemetry beacons when latency spikes occur.
 *
 * Mount once at a high level in an authenticated facility layout.
 */
export function useRealtimeLatencyBeacon(facilityId: string | undefined) {
  const sessionIdRef = useRef<string>(
    `sess-${Math.random().toString(36).substring(2, 15)}`
  );
  // Roll the session-sampling dice exactly once per mount.
  const [sampledIn] = useState(() => Math.random() < BEACON_SESSION_SAMPLE_RATE);
  // Lives outside the effect so the 1-per-minute budget survives effect
  // re-runs (facility/auth changes), not just consecutive events.
  const lastSentAtRef = useRef(0);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    // Sampled-out sessions never report; unauthenticated sessions must never
    // POST at all (no anonymous traffic to the telemetry endpoint).
    if (!facilityId || !sampledIn || !isAuthenticated) return;

    const sendBeacon = async (latencyMs: number) => {
      try {
        await apiClient.post(
          '/telemetry/beacon',
          {
            kind: 'realtime.delivery_latency_spike',
            latencyMs,
            facilityId,
            sessionId: sessionIdRef.current,
          },
          { timeout: 5000 }
        );
      } catch (err: any) {
        // Silently fail — telemetry beacons are best-effort
        console.warn('[useRealtimeLatencyBeacon] Beacon failed', err.message);
      }
    };

    // Listener for worker_update events
    const handleWorkerUpdate = (payload: any) => {
      if (!payload?.timestamp) return;

      // worker_update payloads carry an ISO-8601 string (or epoch ms) timestamp.
      // Parse to ms — using the string directly produced NaN, so the beacon never fired.
      const tsMs =
        typeof payload.timestamp === 'number'
          ? payload.timestamp
          : Date.parse(payload.timestamp);
      if (!Number.isFinite(tsMs)) return;

      const latency = Date.now() - tsMs;

      // Only beacon when latency exceeds 5000ms, rate-limited to 1/min.
      if (latency > 5000) {
        const now = Date.now();
        if (now - lastSentAtRef.current < BEACON_MIN_INTERVAL_MS) return;
        lastSentAtRef.current = now;
        void sendBeacon(latency);
      }
    };

    // Attach listener via wsService.on() — returns unsubscribe function
    const unsubscribe = wsService.on<any>('worker_update', handleWorkerUpdate);

    // Cleanup on unmount or facilityId change
    return () => {
      unsubscribe();
    };
  }, [facilityId, sampledIn, isAuthenticated]);
}
